import { type NFTCollectibleDefinition } from '@capsicum/common';
import { useOngoingUserParticipationsQuery, usePartners } from '@capsicum/socios-core';
import { useCallback } from 'react';

import { type MyCollectible } from '../models';
import { mapPartnerToCollectiblePartner } from '../util';

import type { LockerRoomCampaignReward, LockerRoomUnlock } from '@capsicum/socios-core';

/**
 * @returns sorted list of collectibles owned by user and flexible NFT participations filtered by partner
 */

export const useMyOngoingParticipationsCollectiblesByPartner = (partnerId: string) => {
	const { getPartnerBy } = usePartners();

	return useOngoingUserParticipationsQuery(
		useCallback(
			(data: Array<LockerRoomUnlock> | undefined) => {
				const collectibles =
					data?.reduce((acc: Array<MyCollectible>, currentParticipation) => {
						if (
							currentParticipation.rewards?.[0]?.reward?.nft?.summary?.partnerId === partnerId &&
							currentParticipation?.status === 'accepted'
						) {
							const stackingClosedAt = currentParticipation?.challenge?.campaign?.stackingClosedAt || '';
							const reward: LockerRoomCampaignReward | undefined = currentParticipation.rewards?.[0];
							const partner = mapPartnerToCollectiblePartner(getPartnerBy(p => p.id === partnerId));

							const rewardNft = reward?.reward?.nft as NFTCollectibleDefinition;

							const myCollectible = {
								optionType: currentParticipation.optionType,
								partner,
								summary: {
									...rewardNft.summary,
									collectibleId: reward?.id
								},
								unlockingDate: stackingClosedAt,
								userParticipationId: currentParticipation.id
							} as MyCollectible;

							acc.push(myCollectible);
						}

						return acc;
					}, []) || [];

				return collectibles;
			},
			[partnerId, getPartnerBy]
		),
		{
			direction: 'desc',
			orderBy: 'createdAt',
			'rewardTypes[]': ['nft']
		}
	);
};
